import {ComponentPropsWithoutRef, forwardRef, useId} from 'react';
import styled, {CSSProperties} from 'styled-components';
import {Flexbox, interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {Typography, typographyStyle} from '../Typography';

interface IProps extends ComponentPropsWithoutRef<'input'> {
  containerStyle?: CSSProperties;
  variant?: 'small' | 'large';
  icon?: string;
  leftLabel?: string;
  rightLabel?: string;
  type?: 'checkbox';
  activeBgColor?: string;
  bgColor?: string;
}

export const ToggleSwitch = forwardRef<HTMLInputElement, IProps>(
  (
    {
      checked = false,
      variant,
      icon,
      leftLabel,
      rightLabel,
      containerStyle,
      activeBgColor,
      bgColor,
      ...props
    },
    ref
  ) => {
    const id = useId();
    const hasLabel = !!(leftLabel?.length || rightLabel?.length);
    const hasIcon = !!icon?.length;
    const ballSize = hasIcon
      ? 28
      : hasLabel
      ? 24
      : variant === 'small'
      ? 12
      : 18;

    return (
      <Root
        ballSize={ballSize}
        style={containerStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          ref={ref as any}
          id={id}
          name={id}
          checked={checked}
          hidden
          {...props}
        />
        <Slider
          activeBgColor={activeBgColor}
          bgColor={bgColor}
          color={hasLabel || hasIcon ? 'mono' : 'color'}
        >
          <Ball ballSize={ballSize}>
            {hasIcon && <img src={icon} alt="switch-icon" />}
          </Ball>
          {hasLabel && (
            <LabelWrapper as="span">
              <div className="left-label">{leftLabel ?? ''}</div>
              <div className="right-label">{rightLabel ?? ''}</div>
            </LabelWrapper>
          )}
        </Slider>
      </Root>
    );
  }
);

const padding = 2;

const Root = styled('label').withConfig({
  shouldForwardProp: interceptProps(['ballSize']),
})<{
  ballSize: number;
}>`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 20px;
  ${({ballSize}) =>
    `width: ${ballSize * 2 + padding}px; height: ${ballSize + padding * 2}px;`}
`;

const Ball = styled('span').withConfig({
  shouldForwardProp: interceptProps(['ballSize']),
})<{
  ballSize: number;
}>`
  position: absolute;
  background-color: ${palette.white};
  border-radius: 100%;
  left: ${padding}px;
  top: ${padding}px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 1px 1px 0px #0000001a;
  ${({ballSize}) => `width: ${ballSize}px; height: ${ballSize}px;`}
  img {
    width: 16px;
    height: 16px;
  }
`;

const LabelWrapper = styled(Flexbox)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${padding}px;
  & > div {
    flex-grow: 1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: ${palette.white};
    ${{...typographyStyle.medium14}}
  }
`;

const Slider = styled('span').withConfig({
  shouldForwardProp: interceptProps(['color', 'activeBgColor', 'bgColor']),
})<{
  color: 'mono' | 'color';
  activeBgColor?: string;
  bgColor?: string;
}>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${padding}px;
  border-radius: 15px;
  ${({color, bgColor}) =>
    bgColor
      ? `background-color: ${bgColor};`
      : color === 'mono'
      ? `background-color: #3E4650;`
      : `background-color: ${palette.gray150};`}
  .left-label {
    color: ${palette.textPrimary};
  }
  input[type='checkbox']:checked + & {
    ${({color, activeBgColor}) =>
      color !== 'mono' &&
      `background-color: ${activeBgColor || palette.teal100};`}
    ${Ball} {
      left: auto;
      right: ${padding}px;
    }
    .right-label {
      color: ${palette.textPrimary};
    }
    .left-label {
      color: ${palette.white};
    }
  }
  input[type='checkbox']:disabled + & {
    ${({color}) =>
      color === 'mono'
        ? `opacity: .8;`
        : `background-color: ${palette.gray150};
      ${Ball} {
        box-shadow: none;
      }`}
  }
  input[type='checkbox']:disabled + & {
    cursor: default;
  }
`;

export const ToggleSwitchLabel = styled(Typography).attrs({
  variant: 'medium12',
  as: 'label',
})`
  cursor: pointer;
  flex-shrink: 0;
`;

export const ToggleSwitchItem = styled(Flexbox).withConfig({
  shouldForwardProp: interceptProps(['disabled']),
})<{
  disabled: boolean;
}>`
  ${Root} + ${ToggleSwitchLabel},
  ${ToggleSwitchLabel} + ${Root} {
    margin-left: 4px;
  }
  ${({disabled}) =>
    disabled &&
    `
    ${ToggleSwitchLabel} {
      color: ${palette.disabled};
    }
  `}
`;
